import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import { Activities } from "../../types/UserActivityInterfaces";

interface ActivityMessageProps {
  activity: Activities;
  workspaceID: string;
}

const ViewLink = ({
  baseLink,
  viewID,
  viewName,
  icon,
}: {
  baseLink: string;
  viewID: string;
  viewName: string;
  icon?: string;
}) => {
  const viewLink = baseLink.replace("None", viewID);

  return (
    <Link to={viewLink}>
      <span className="text-primary">
        {icon && (
          <i
            className={`mdi mdi-${icon} mr-1`}
            style={{ fontSize: 18, top: 2, position: "relative" }}
          />
        )}
        {viewName}
      </span>
    </Link>
  );
};

const PageLink = ({
  pageLink,
  pageName,
}: {
  pageLink: string;
  pageName: string;
}) => {
  return (
    <Link to={pageLink} className="text-primary">
      <i
        className="mdi mdi-file-table-box-outline mr-1"
        style={{ fontSize: 18, top: 2, position: "relative" }}
      />
      {pageName}
    </Link>
  );
};

export const pageTypeIconMap: { [key: string]: string } = {
  Table: "table",
  Dashboard: "view-dashboard-outline",
  Document: "text-box-outline",
};

const ActivityMessage = ({ activity, workspaceID }: ActivityMessageProps) => {
  const tenantID = useAtomValue(tenantIDAtom);
  const type = activity.Type;

  const pageID = activity?.Page?.id;
  const pageLink = `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${pageID}/views/None`;

  if (type === "edited-record-in-page") {
    const record = activity?.Record;
    const view = activity?.View;
    const drawersObject = [
      {
        id: record?.id,
        module: { APIName: record._ModuleAPIName },
        ...(view && { vID: view?.id }),
        tab: "Summary",
      },
    ];
    const urlParams = new URLSearchParams();
    const drawerStr = JSON.stringify(drawersObject);
    urlParams.set("open", drawerStr);
    const url = pageLink.replace("None", activity.View?.id);
    const recordURL = url + "?" + urlParams.toString();

    return (
      <div>
        Record{" "}
        <Link to={recordURL}>
          <span className="text-primary">{activity.Record._RecordName}</span>
        </Link>
        &nbsp;updated in&nbsp;
        <ViewLink
          icon="table"
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;on <PageLink pageLink={pageLink} pageName={activity.Page.Name} />
        &nbsp;
      </div>
    );
  }

  if (type === "viewed-page-view") {
    return (
      <div>
        Viewed{" "}
        <ViewLink
          icon={pageTypeIconMap[activity.View?.Type]}
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;on <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;
      </div>
    );
  }

  if (type === "edited-dashboard-view") {
    return (
      <div>
        Edited{" "}
        <ViewLink
          icon="view-dashboard-outline"
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;on <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;
      </div>
    );
  }

  if (type === "queried-page-view-table") {
    return (
      <div>
        Filtered Records in{" "}
        <ViewLink
          icon="table"
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;on <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;
      </div>
    );
  }

  if (type === "edited-page") {
    return (
      <div>
        Edited <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;Page.
      </div>
    );
  }

  if (type === "edited-document-view") {
    return (
      <div>
        Edited{" "}
        <ViewLink
          icon="text-box-outline"
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;on <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;Page.
      </div>
    );
  }

  if (type === "bulk-update-in-page") {
    return (
      <div>
        Bulk updated records in{" "}
        <ViewLink
          icon="table"
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;on <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;Page.
      </div>
    );
  }

  if (type === "bulk-delete-in-page") {
    return (
      <div>
        Bulk deleted records in{" "}
        <ViewLink
          icon="table"
          baseLink={pageLink}
          viewID={activity.View?.id}
          viewName={activity.View?.Name}
        />
        &nbsp;View, on{" "}
        <PageLink pageLink={pageLink} pageName={activity.Page?.Name} />
        &nbsp;Page.
      </div>
    );
  }

  return <div>Unknown Activity</div>;
};

export default ActivityMessage;
